import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { bookingTranslations } from '../translations/booking';
import { useSeo } from '../hooks/useSeo';

export const Booking = () => {
  const { language } = useLanguage();
  const t = bookingTranslations[language];

  const isFr = language === 'fr';
  useSeo(
    isFr ? "Prendre rendez-vous | Numérologie & accompagnement avec Stef Ruiz" : "Comprendre qui l'on est - Stef Ruiz",
    isFr ? "Réservez un appel découverte ou une séance avec Stef Ruiz pour un accompagnement en numérologie et transitions de vie, à Ibiza ou en visio." : "A product of emergent.sh"
  );

  useEffect(() => {
    // Load Calendly widget script
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);

    // Add custom CSS to hide Calendly badge and customize
    const style = document.createElement('style');
    style.innerHTML = `
      .calendly-badge-widget,
      .calendly-badge-content,
      [data-calendly-badge],
      .calendly-inline-widget div[class*="badge"],
      .calendly-inline-widget div[class*="branding"] {
        display: none !important;
        opacity: 0 !important;
        visibility: hidden !important;
        width: 0 !important;
        height: 0 !important;
        overflow: hidden !important;
      }
      
      .calendly-inline-widget {
        border-radius: 12px;
        overflow: hidden;
        position: relative;
      }
      
      .calendly-inline-widget::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 120px;
        height: 80px;
        background: white;
        z-index: 999;
        pointer-events: none;
      }
      
      .calendly-inline-widget iframe {
        border-radius: 12px;
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section with Photo */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(/stef-pointing-ibiza.jpg)',
            backgroundPosition: '50% 60%',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900/50 via-gray-900/40 to-gray-900/50"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-light text-white mb-6 leading-tight">
            {t.hero.title}
          </h1>
          <p className="text-lg md:text-xl text-white/95 leading-relaxed font-light max-w-3xl mx-auto mb-4">
            {t.hero.subtitle1}
          </p>
          <p className="text-base md:text-lg text-white/90 leading-relaxed font-light">
            {t.hero.subtitle2}
          </p>
        </div>
      </section>

      {/* Calendly Widget - Full Width */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-teal-50 via-white to-pink-50">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-2xl shadow-2xl p-4 sm:p-6 border-2 border-teal-200 relative">
            {/* Calendly Inline Widget */}
            <div 
              className="calendly-inline-widget" 
              data-url="https://calendly.com/stefruiz04/appel-decouverte-gratuit?hide_event_type_details=1&hide_gdpr_banner=1&primary_color=14b8a6"
              style={{ minWidth: '320px', height: '800px', position: 'relative' }}
            ></div>
          </div>
          
          {/* Info sous le calendrier */}
          <div className="mt-8 text-center bg-white rounded-lg shadow-md p-8 border border-teal-100">
            <h3 className="text-2xl font-light text-teal-600 mb-4">
              {t.calendly.title}
            </h3>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              {t.calendly.description1}
            </p>
            <p className="text-base text-gray-600 leading-relaxed">
              {t.calendly.description2}
            </p>
            <p className="text-xs text-gray-500 leading-relaxed mt-6" data-testid="booking-privacy-note">
              {t.calendly.privacyBefore}
              <Link to="/confidentialite" target="_blank" className="text-teal-600 underline hover:text-teal-700">
                {t.calendly.privacyLink}
              </Link>
              {t.calendly.privacyAfter}
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-light text-center text-teal-600 mb-10">
            {t.whyBook.title}
          </h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center">
                <CheckCircle2 size={20} className="text-white" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-teal-600 mb-1">{t.whyBook.reason1}</h3>
                <p className="text-gray-600 leading-relaxed">{t.whyBook.reason1desc}</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-pink-600 flex items-center justify-center">
                <CheckCircle2 size={20} className="text-white" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-teal-600 mb-1">{t.whyBook.reason2}</h3>
                <p className="text-gray-600 leading-relaxed">{t.whyBook.reason2desc}</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-pink-500 flex items-center justify-center">
                <CheckCircle2 size={20} className="text-white" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-teal-600 mb-1">{t.whyBook.reason3}</h3>
                <p className="text-gray-600 leading-relaxed">{t.whyBook.reason3desc}</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-pink-500 flex items-center justify-center">
                <CheckCircle2 size={20} className="text-white" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-teal-600 mb-1">{t.whyBook.reason4}</h3>
                <p className="text-gray-600 leading-relaxed">{t.whyBook.reason4desc}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-teal-50 to-pink-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-light text-center text-teal-600 mb-12">
            {t.process.title}
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center mx-auto mb-4 text-white text-2xl font-light shadow-lg">
                1
              </div>
              <h3 className="text-xl font-medium text-teal-600 mb-3">{t.process.step1.title}</h3>
              <p className="text-gray-700 leading-relaxed">
                {t.process.step1.description}
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-pink-500 to-pink-600 flex items-center justify-center mx-auto mb-4 text-white text-2xl font-light shadow-lg">
                2
              </div>
              <h3 className="text-xl font-medium text-teal-600 mb-3">{t.process.step2.title}</h3>
              <p className="text-gray-700 leading-relaxed">
                {t.process.step2.description}
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center mx-auto mb-4 text-white text-2xl font-light shadow-lg">
                3
              </div>
              <h3 className="text-xl font-medium text-teal-600 mb-3">{t.process.step3.title}</h3>
              <p className="text-gray-700 leading-relaxed">
                {t.process.step3.description}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final Reassurance */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <div className="relative">
            <svg className="absolute top-0 left-0 w-10 h-10 text-teal-200 -mt-6 -ml-6" fill="currentColor" viewBox="0 0 32 32">
              <path d="M10 8c-3.3 0-6 2.7-6 6s2.7 6 6 6c.4 0 .8 0 1.2-.1L14 24v-5.1c2.3-1.1 4-3.5 4-6.3 0-3.8-3.1-6.9-6.9-6.9L10 8zm12 0c-.4 0-.8 0-1.2.1L18 4v5.1C15.7 10.2 14 12.6 14 15.4c0 3.8 3.1 6.9 6.9 6.9l.1-.1c3.3 0 6-2.7 6-6s-2.7-6-6-6z"/>
            </svg>
            <p className="text-xl md:text-2xl text-gray-700 leading-relaxed font-light italic px-4 sm:px-10">
              {t.quote.text}
            </p>
            <p className="text-lg text-teal-600 mt-6 font-light">{t.quote.author}</p>
          </div>
        </div>
      </section>
    </div>
  );
};
